import { CorporateStructureSortMode, IntegrationSource, RequestStatus, TrackingLevel, defaults, entities } from 'typed/api';
import { Action, UntypedAction } from 'typed/actions';
import {
    IReducer, childrenReducer, combineReducers, defaultReducer, entityAddReducer, entityDeleteReducer, entityEditAllReducer, entityEditReducer,
    entityLoadAllReducer, entityLoadReducer, sortedEntitiesReducer, switchReducers, synchronizableEnumReducer
} from './utils';
import { CORPORATE_STRUCTURE_STORE_KEYS_MAP } from 'typed/components/corporate-structure/utils';
import { deepAssign, toArray } from 'typed/utils';

const
    ls = defaults.NetworkArrayOf(defaults.TrackingLevel),
    ps = defaults.NetworkArrayOf(defaults.Product),
    gs = defaults.NetworkArrayOf(defaults.GeoArea),
    cs = defaults.NetworkArrayOf(defaults.Customer),
    l1s = defaults.NetworkArrayOf(defaults.TrackingLevel1Item),
    l2s = defaults.NetworkArrayOf(defaults.TrackingLevel2Item),
    l3s = defaults.NetworkArrayOf(defaults.TrackingLevel3Item),
    l4s = defaults.NetworkArrayOf(defaults.TrackingLevel4Item),
    les = defaults.NetworkArrayOf(defaults.CompanyLegalEntity),
    f = defaults.CompanyFunction,
    fs = defaults.NetworkArrayOf(f) as entities.CompanyFunctionNetworkArray,
    vts = defaults.NetworkArrayOf(defaults.CompanyVirtualTeam),
    vt = defaults.CompanyVirtualTeam,
    vtms = defaults.NetworkArrayOf(defaults.CompanyVirtualTeamMember),
    esps = defaults.ERPSyncProductReportERPSyncItemPaginationSlice,
    espss = [esps].slice(0, 0),
    escs = defaults.ERPSyncCustomerReportERPSyncItemPaginationSlice,
    escss = [escs].slice(0, 0),


    corporateStructure = combineReducers({
        trackingLevels: switchReducers(ls,
            entityLoadAllReducer(ls, 'LOAD_ALL_TRACKING_LEVELS', TrackingLevel as any, 'TrackingLevel'),
            entityEditReducer(ls, 'EDIT_TRACKING_LEVEL'),
            (state: entities.NetworkArray<entities.TrackingLevel> = ls, action: Action<'ACTIVATE_ERP_DIMENSION_MAPPING_DIMENSION'>) =>
                action.type === 'ACTIVATE_ERP_DIMENSION_MAPPING_DIMENSION' && action.payload.status === RequestStatus.SUCCEEDED && !!action.payload.trackingLevelName ?
                    deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } }) :
                    state
        ),
        companyLegalEntityTypes: entityLoadAllReducer(defaults.NetworkArray, 'LOAD_ALL_COMPANY_LEGAL_ENTITY_TYPES'),
        corporateStructureSortModes: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.CorporateStructureSortMode), 'LOAD_ALL_CORPORATE_STRUCTURE_SORT_MODES', CorporateStructureSortMode as any, 'CorporateStructureSortMode'),
        [CORPORATE_STRUCTURE_STORE_KEYS_MAP[TrackingLevel.PRODUCT] as 'products']: sortedEntitiesReducer(ps, 'Name', switchReducers(ps,
            entityLoadAllReducer(ps, 'LOAD_ALL_PRODUCTS'),
            entityLoadReducer(ps, 'LOAD_PRODUCT'),
            entityLoadReducer(ps, 'FIND_OR_CREATE_DEFAULT_PRODUCT'),
            entityAddReducer(ps, 'ADD_PRODUCT'),
            entityEditAllReducer(ps, 'EDIT_ALL_PRODUCTS'),
            entityEditReducer(ps, 'EDIT_PRODUCT'),
            entityDeleteReducer(ps, 'DELETE_PRODUCT')
        )),
        [CORPORATE_STRUCTURE_STORE_KEYS_MAP[TrackingLevel.GEO_AREA] as 'geoAreas']: sortedEntitiesReducer(gs, 'Name', switchReducers(gs,
            entityLoadAllReducer(gs, 'LOAD_ALL_GEO_AREAS'),
            entityLoadReducer(gs, 'LOAD_GEO_AREA'),
            entityLoadReducer(gs, 'FIND_OR_CREATE_DEFAULT_GEO_AREA'),
            entityAddReducer(gs, 'ADD_GEO_AREA'),
            entityEditAllReducer(gs, 'EDIT_ALL_GEO_AREAS'),
            entityEditReducer(gs, 'EDIT_GEO_AREA'),
            entityDeleteReducer(gs, 'DELETE_GEO_AREA')
        )),
        [CORPORATE_STRUCTURE_STORE_KEYS_MAP[TrackingLevel.CUSTOMER] as 'customers']: sortedEntitiesReducer(cs, 'Name', switchReducers(cs,
            entityLoadAllReducer(cs, 'LOAD_ALL_CUSTOMERS'),
            entityLoadReducer(cs, 'LOAD_CUSTOMER'),
            entityLoadReducer(cs, 'FIND_OR_CREATE_DEFAULT_CUSTOMER'),
            entityAddReducer(cs, 'ADD_CUSTOMER'),
            entityEditAllReducer(cs, 'EDIT_ALL_CUSTOMERS'),
            entityEditReducer(cs, 'EDIT_CUSTOMER'),
            entityDeleteReducer(cs, 'DELETE_CUSTOMER')
        )),
        [CORPORATE_STRUCTURE_STORE_KEYS_MAP[TrackingLevel.TRACKING_LEVEL_1] as 'trackingLevel1Items']: sortedEntitiesReducer(l1s, 'Name', switchReducers(l1s,
            entityLoadAllReducer(l1s, 'LOAD_ALL_TRACKING_LEVEL_1_ITEMS'),
            entityLoadReducer(l1s, 'LOAD_TRACKING_LEVEL_1_ITEM'),
            entityLoadReducer(l1s, 'FIND_OR_CREATE_DEFAULT_TRACKING_LEVEL_1_ITEM'),
            entityAddReducer(l1s, 'ADD_TRACKING_LEVEL_1_ITEM'),
            entityEditAllReducer(l1s, 'EDIT_ALL_TRACKING_LEVEL_1_ITEMS'),
            entityEditReducer(l1s, 'EDIT_TRACKING_LEVEL_1_ITEM'),
            entityDeleteReducer(l1s, 'DELETE_TRACKING_LEVEL_1_ITEM')
        )),
        [CORPORATE_STRUCTURE_STORE_KEYS_MAP[TrackingLevel.TRACKING_LEVEL_2] as 'trackingLevel2Items']: sortedEntitiesReducer(l2s, 'Name', switchReducers(l2s,
            entityLoadAllReducer(l2s, 'LOAD_ALL_TRACKING_LEVEL_2_ITEMS'),
            entityLoadReducer(l2s, 'LOAD_TRACKING_LEVEL_2_ITEM'),
            entityLoadReducer(l2s, 'FIND_OR_CREATE_DEFAULT_TRACKING_LEVEL_2_ITEM'),
            entityAddReducer(l2s, 'ADD_TRACKING_LEVEL_2_ITEM'),
            entityEditAllReducer(l2s, 'EDIT_ALL_TRACKING_LEVEL_2_ITEMS'),
            entityEditReducer(l2s, 'EDIT_TRACKING_LEVEL_2_ITEM'),
            entityDeleteReducer(l2s, 'DELETE_TRACKING_LEVEL_2_ITEM')
        )),
        [CORPORATE_STRUCTURE_STORE_KEYS_MAP[TrackingLevel.TRACKING_LEVEL_3] as 'trackingLevel3Items']: sortedEntitiesReducer(l3s, 'Name', switchReducers(l3s,
            entityLoadAllReducer(l3s, 'LOAD_ALL_TRACKING_LEVEL_3_ITEMS'),
            entityLoadReducer(l3s, 'LOAD_TRACKING_LEVEL_3_ITEM'),
            entityLoadReducer(l3s, 'FIND_OR_CREATE_DEFAULT_TRACKING_LEVEL_3_ITEM'),
            entityAddReducer(l3s, 'ADD_TRACKING_LEVEL_3_ITEM'),
            entityEditAllReducer(l3s, 'EDIT_ALL_TRACKING_LEVEL_3_ITEMS'),
            entityEditReducer(l3s, 'EDIT_TRACKING_LEVEL_3_ITEM'),
            entityDeleteReducer(l3s, 'DELETE_TRACKING_LEVEL_3_ITEM')
        )),
        [CORPORATE_STRUCTURE_STORE_KEYS_MAP[TrackingLevel.TRACKING_LEVEL_4] as 'trackingLevel4Items']: sortedEntitiesReducer(l4s, 'Name', switchReducers(l4s,
            entityLoadAllReducer(l4s, 'LOAD_ALL_TRACKING_LEVEL_4_ITEMS'),
            entityLoadReducer(l4s, 'LOAD_TRACKING_LEVEL_4_ITEM'),
            entityLoadReducer(l4s, 'FIND_OR_CREATE_DEFAULT_TRACKING_LEVEL_4_ITEM'),
            entityAddReducer(l4s, 'ADD_TRACKING_LEVEL_4_ITEM'),
            entityEditAllReducer(l4s, 'EDIT_ALL_TRACKING_LEVEL_4_ITEMS'),
            entityEditReducer(l4s, 'EDIT_TRACKING_LEVEL_4_ITEM'),
            entityDeleteReducer(l4s, 'DELETE_TRACKING_LEVEL_4_ITEM')
        )),
        [CORPORATE_STRUCTURE_STORE_KEYS_MAP[TrackingLevel.COMPANY_LEGAL_ENTITY] as 'companyLegalEntities']: sortedEntitiesReducer(les, 'Name', switchReducers(les,
            entityLoadAllReducer(les, 'LOAD_ALL_COMPANY_LEGAL_ENTITIES'),
            entityLoadReducer(les, 'LOAD_COMPANY_LEGAL_ENTITY'),
            entityLoadReducer(les, 'FIND_OR_CREATE_DEFAULT_COMPANY_LEGAL_ENTITY'),
            entityAddReducer(les, 'ADD_COMPANY_LEGAL_ENTITY'),
            entityEditReducer(les, 'EDIT_COMPANY_LEGAL_ENTITY'),
            entityDeleteReducer(les, 'DELETE_COMPANY_LEGAL_ENTITY'),
            (state = les, action: UntypedAction) => {
                if (action.companyLegalEntityID && (
                    action.type === 'UNLINK_FINANCIAL_SYSTEM_BATCHES_FROM_LEGAL_ENTITY_SUCCESS' ||
                    action.type === 'DELETE_ERP_NETVISOR_INTEGRATION_SETTINGS_SUCCESS'
                )) {
                    return deepAssign(state, {
                        Data: {
                            [state.Data.getIndexByID(action.companyLegalEntityID)]: {
                                FinancialIntegrationSourceID: null
                            }
                        }
                    });
                }

                if (action.type === 'IMPORT_AND_PROCESS_INTEGRATION_FINANCIAL_FILES_SUCCESS' && !action.startImmediately) {
                    let entity = state.Data.getByID(action.legalEntityID);
                    const entityIndex = state.Data.getIndexByID(action.legalEntityID);

                    if (entity && entityIndex >= 0) {
                        entity = deepAssign(entity, {
                            Custom: {
                                pendingCalculationSources: (entity.Custom.pendingCalculationSources || []).concat([action.sourceID as IntegrationSource]).unique()
                            }
                        });

                        return deepAssign(state, {
                            Data: {
                                [entityIndex]: entity
                            }
                        });
                    }
                }

                if (action.type === 'DEMAND_DATA_FROM_INTEGRATION_SOURCE_SUCCESS' ||
                    action.type === 'IMPORT_AND_PROCESS_INTEGRATION_FINANCIAL_FILES_SUCCESS' && action.startImmediately) {
                    const integrationSourceID = action.type === 'DEMAND_DATA_FROM_INTEGRATION_SOURCE_SUCCESS' ? action.integrationSourceID : action.sourceID;

                    return deepAssign(state, {
                        Data: state.Data.map(entity => deepAssign(entity, {
                            Custom: {
                                pendingCalculationSources: (entity.Custom.pendingCalculationSources || []).filter(sourceID => sourceID !== integrationSourceID)
                            }
                        }))
                    });
                }

                if (action.type === 'VOID_ALL_COMPANY_LEGAL_ENTITIES') {
                    return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });
                }

                return state;
            }
        )),
        [CORPORATE_STRUCTURE_STORE_KEYS_MAP[TrackingLevel.COMPANY_FUNCTION] as 'companyFunctions']: switchReducers(fs,
            entityLoadAllReducer(fs, 'LOAD_ALL_COMPANY_FUNCTIONS'),
            entityLoadReducer(fs, 'LOAD_COMPANY_FUNCTION'),
            entityLoadReducer(fs, 'FIND_OR_CREATE_DEFAULT_COMPANY_FUNCTION'),
            entityAddReducer(fs, 'ADD_COMPANY_FUNCTION'),
            entityEditReducer(fs, 'EDIT_COMPANY_FUNCTION'),
            entityDeleteReducer(fs, 'DELETE_COMPANY_FUNCTION'),
            combineReducers(fs, {
                Data: state => (state || fs.Data).copy().sortBy('Name'),
                Custom: (state = fs.Custom, action: Action<'SET_USER_CUSTOM_ROADMAP_RANGE'>) =>
                    action.type === 'SET_USER_CUSTOM_ROADMAP_RANGE' ?
                        deepAssign(state, {
                            roadmapStartDate: action.payload.startDate,
                            roadmapEndDate: action.payload.endDate
                        }) :
                        state
            }),
            childrenReducer(fs,
                combineReducers(f, {
                    Custom: (state = f.Custom, action: Action<'SET_TEAM_CUSTOM_ROADMAP_RANGE' | 'SET_COMPANY_FUNCTION_CUSTOM_JOURNEY_RANGE'>) => {
                        switch (action.type) {
                            case 'SET_TEAM_CUSTOM_ROADMAP_RANGE':
                                return deepAssign(state, {
                                    roadmapStartDate: action.payload.startDate,
                                    roadmapEndDate: action.payload.endDate
                                });

                            case 'SET_COMPANY_FUNCTION_CUSTOM_JOURNEY_RANGE':
                                return deepAssign(state, {
                                    journeyStartDate: action.payload.startDate,
                                    journeyEndDate: action.payload.endDate
                                });

                            default:
                                return state;
                        }
                    }
                }),
                (state, action) => state.Data.getIndexByID(action.payload && (action.payload as any).entityID)
            ),
            (state = fs, action: UntypedAction) => {
                switch (action.type) {
                    case 'DELETE_COMPANY_LEGAL_ENTITY':
                        if (action.payload.status === RequestStatus.SUCCEEDED) {
                            return deepAssign(state, { Data: state.Data.map(f => f.CompanyLegalEntityID === action.payload.entityID ? { ...f, CompanyLegalEntityID: null } : f) })
                        }
                        return state;

                    case 'VOID_ALL_COMPANY_FUNCTIONS':
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });

                    default:
                        return state;
                }
            }
        ),
        companyVirtualTeams: switchReducers(vts,
            entityLoadAllReducer(vts, 'LOAD_ALL_COMPANY_VIRTUAL_TEAMS'),
            entityLoadReducer(vts, 'LOAD_COMPANY_VIRTUAL_TEAM'),
            entityAddReducer(vts, 'ADD_COMPANY_VIRTUAL_TEAM'),
            entityEditReducer(vts, 'EDIT_COMPANY_VIRTUAL_TEAM'),
            entityDeleteReducer(vts, 'DELETE_COMPANY_VIRTUAL_TEAM'),
            childrenReducer(vts,
                combineReducers(vt, {
                    Custom: (state = vt.Custom, action: Action<'SET_COMPANY_VIRTUAL_TEAM_CUSTOM_JOURNEY_RANGE'>) => {
                        return action.type === 'SET_COMPANY_VIRTUAL_TEAM_CUSTOM_JOURNEY_RANGE' ?
                            deepAssign(state, {
                                journeyStartDate: action.payload.startDate,
                                journeyEndDate: action.payload.endDate
                            }) :
                            state;
                    }
                }),
                (state, action) => state.Data.getIndexByID(action.payload && (action.payload as any).entityID)
            ),
            (state = vts, action: UntypedAction) => {
                switch (action.type) {
                    case 'VOID_ALL_COMPANY_VIRTUAL_TEAMS':
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });

                    default:
                        return state;
                }
            }
        ),
        companyVirtualTeamMembers: switchReducers(vtms,
            entityLoadAllReducer(vtms, 'LOAD_ALL_COMPANY_VIRTUAL_TEAM_MEMBERS'),
            entityLoadReducer(vtms, 'LOAD_COMPANY_VIRTUAL_TEAM_MEMBER'),
            entityAddReducer(vtms, 'ADD_COMPANY_VIRTUAL_TEAM_MEMBER'),
            entityEditReducer(vtms, 'EDIT_COMPANY_VIRTUAL_TEAM_MEMBER'),
            entityDeleteReducer(vtms, 'DELETE_COMPANY_VIRTUAL_TEAM_MEMBER'),
            (state = vtms, action: Action<'DELETE_COMPANY_VIRTUAL_TEAM'>) =>
                action.type === 'DELETE_COMPANY_VIRTUAL_TEAM' && action.payload.status === RequestStatus.SUCCEEDED ?
                    deepAssign(state, { Data: state.Data.filter(member => (member as any).TeamID !== action.payload.entityID) }) :
                    state,
            (state = vtms, action: UntypedAction) => {
                switch (action.type) {
                    case 'VOID_ALL_COMPANY_VIRTUAL_TEAM_MEMBERS':
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });

                    default:
                        return state;
                }
            }
        ),
        erpSyncProducts: (state: typeof espss = espss, action: Action<'SEARCH_ALL_ERP_SYNC_PRODUCTS_SLICES' | 'SEARCH_ERP_SYNC_PRODUCTS_SLICE' | 'EDIT_ALL_ERP_SYNC_PRODUCTS' | 'VOID_ALL_ERP_SYNC_PRODUCTS_SLICES'>): entities.ERPSyncItemPaginationSlice<entities.ERPSyncProductReport>[] => {
            switch (action.type) {
                case 'SEARCH_ALL_ERP_SYNC_PRODUCTS_SLICES':
                    const ssnar: IReducer<entities.ERPSyncItemPaginationSlice<entities.ERPSyncProductReport>, any> =
                         entityLoadAllReducer(esps, 'SEARCH_ERP_SYNC_PRODUCTS_SLICE');

                    return deepAssign(state, state.fullJoin(
                        action.payload.status !== RequestStatus.SUCCEEDED ? action.payload.templates : action.payload.response,
                        oldData => `${oldData.ParentGroupID}_${oldData.IsActive}`,
                        newData => `${newData.ParentGroupID}_${newData.IsActive}`,
                        (oldData, newData) => !newData ?
                            oldData! :
                            ssnar(
                                oldData || { ...esps, ...newData, Data: [] },
                                {
                                    type: 'SEARCH_ERP_SYNC_PRODUCTS_SLICE',
                                    payload: { ...action.payload, response: newData, startID: -1 }
                                }
                            )
                    ));

                case 'SEARCH_ERP_SYNC_PRODUCTS_SLICE':
                    const snar: IReducer<entities.ERPSyncItemPaginationSlice<entities.ERPSyncProductReport>, any> =
                     entityLoadAllReducer(esps, 'SEARCH_ERP_SYNC_PRODUCTS_SLICE');

                    return deepAssign(state, state.fullJoin(
                        toArray(action.payload.status !== RequestStatus.SUCCEEDED ? action.payload.template : action.payload.response),
                        oldData => `${oldData.ParentGroupID}_${oldData.IsActive}`,
                        newData => `${newData.ParentGroupID}_${newData.IsActive}`,
                        (oldData, newData) => !newData ?
                            oldData! :
                            snar(
                                oldData || { ...esps, ...newData, Data: [] },
                                {
                                    type: 'SEARCH_ERP_SYNC_PRODUCTS_SLICE',
                                    payload: { ...action.payload, response: newData, startID: -1 }
                                }
                            )
                    ));

                case 'EDIT_ALL_ERP_SYNC_PRODUCTS':
                    return action.payload.status === RequestStatus.SUCCEEDED ?
                        deepAssign(state, state.filter(slice =>
                            !action.payload.erpSyncProducts.some(product =>
                                !!slice.Data.getByID(product.ID) ||
                                slice.ParentGroupID === product.ParentGroupID
                            )
                        )) :
                        state;

                case 'VOID_ALL_ERP_SYNC_PRODUCTS_SLICES':
                    return deepAssign(state, state.filter(slice =>
                        !action.payload.templates.some(template =>
                            (slice.ParentGroupID || null) === (template.ParentGroupID || null)
                        )
                    ));

                default:
                    return defaultReducer(state, action);
            }
        },
        erpSyncCustomers: (state: typeof escss = escss, action: Action<'SEARCH_ALL_ERP_SYNC_CUSTOMERS_SLICES' | 'SEARCH_ERP_SYNC_CUSTOMERS_SLICE' | 'EDIT_ALL_ERP_SYNC_CUSTOMERS' | 'VOID_ALL_ERP_SYNC_CUSTOMERS_SLICES'>): entities.ERPSyncItemPaginationSlice<entities.ERPSyncCustomerReport>[] => {
            switch (action.type) {
                case 'SEARCH_ALL_ERP_SYNC_CUSTOMERS_SLICES':
                    const ssnar: IReducer<entities.ERPSyncItemPaginationSlice<entities.ERPSyncCustomerReport>, any> =
                     entityLoadAllReducer(escs, 'SEARCH_ERP_SYNC_CUSTOMERS_SLICE');

                    return deepAssign(state, state.fullJoin(
                        action.payload.status !== RequestStatus.SUCCEEDED ? action.payload.templates : action.payload.response,
                        oldData => `${oldData.ParentGroupID}_${oldData.IsActive}`,
                        newData => `${newData.ParentGroupID}_${newData.IsActive}`,
                        (oldData, newData) => !newData ?
                            oldData! :
                            ssnar(
                                oldData || { ...escs, ...newData, Data: [] },
                                {
                                    type: 'SEARCH_ERP_SYNC_CUSTOMERS_SLICE',
                                    payload: { ...action.payload, response: newData, startID: -1 }
                                }
                            )
                    ));

                case 'SEARCH_ERP_SYNC_CUSTOMERS_SLICE':
                    const snar: IReducer<entities.ERPSyncItemPaginationSlice<entities.ERPSyncCustomerReport>, any> =
                     entityLoadAllReducer(escs, 'SEARCH_ERP_SYNC_CUSTOMERS_SLICE');

                    return deepAssign(state, state.fullJoin(
                        toArray(action.payload.status !== RequestStatus.SUCCEEDED ? action.payload.template : action.payload.response),
                        oldData => `${oldData.ParentGroupID}_${oldData.IsActive}`,
                        newData => `${newData.ParentGroupID}_${newData.IsActive}`,
                        (oldData, newData) => !newData ?
                            oldData! :
                            snar(
                                oldData || { ...escs, ...newData, Data: [] },
                                {
                                    type: 'SEARCH_ERP_SYNC_CUSTOMERS_SLICE',
                                    payload: { ...action.payload, response: newData, startID: -1 }
                                }
                            )
                    ));

                case 'EDIT_ALL_ERP_SYNC_CUSTOMERS':
                    return action.payload.status === RequestStatus.SUCCEEDED ?
                        deepAssign(state, state.filter(slice =>
                            !action.payload.erpSyncCustomers.some(customer =>
                                !!slice.Data.getByID(customer.ID) ||
                            slice.ParentGroupID === customer.ParentGroupID
                            )
                        )) :
                        state;

                case 'VOID_ALL_ERP_SYNC_CUSTOMERS_SLICES':
                    return deepAssign(state, state.filter(slice =>
                        !action.payload.templates.some(template =>
                            (slice.ParentGroupID || null) === (template.ParentGroupID || null)
                        )
                    ));

                default:
                    return defaultReducer(state, action);
            }
        }
    }),
    def: ReturnType<typeof corporateStructure> = corporateStructure(undefined, {} as UntypedAction);

export default switchReducers(def,
    corporateStructure,
    (state = def, action: Action<'VOID_CORPORATE_STRUCTURE'>) => action.type === 'VOID_CORPORATE_STRUCTURE' ? def : state
);
